import { useEffect, useState } from "react";
import { Users, FileDown, FileSpreadsheet, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiErrorMessage, API, getToken } from "@/lib/api";

const BULAN = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export default function AdminDemografi() {
  const now = new Date();
  const [bulan, setBulan] = useState(String(now.getMonth() + 1));
  const [tahun, setTahun] = useState(String(now.getFullYear()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  const periode = `${tahun}-${String(bulan).padStart(2, "0")}`;

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demografi", { params: { bulan: periode } });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode]);

  async function doExport(kind) {
    setExporting(kind);
    try {
      const url = `${API}/admin/demografi.${kind}?bulan=${periode}`;
      const token = getToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Gagal mengunduh");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Demografi_${periode}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`Laporan ${kind.toUpperCase()} berhasil diunduh`);
    } catch (e) {
      toast.error(e.message || "Gagal mengunduh");
    } finally {
      setExporting(null);
    }
  }

  const periodeLabel = data?.label || `${BULAN[Number(bulan) - 1]} ${tahun}`;

  // Buat rows yang akan ditampilkan di tabel.
  const buildRows = () => {
    if (!data) return [];
    const rows = [];
    rows.push({ kind: "heading", label: "Kelompok Masa Kerja" });
    rows.push({ kind: "row", label: "< 1 Thn", val: data.masa_kerja.rows["< 1 Thn"] || 0 });
    rows.push({ kind: "row", label: "> 1 Thn", val: data.masa_kerja.rows[">= 1 Thn"] || 0 });
    rows.push({ kind: "sum", label: "Jumlah", val: data.masa_kerja.jumlah });
    rows.push({ kind: "heading", label: "Kelompok Usia" });
    ["20-24", "25-29", "30-34", "35-39", "40-44", "45-49", "50-55", ">55"].forEach((k) => {
      rows.push({ kind: "row", label: k, val: data.usia.rows[k] || 0 });
    });
    rows.push({ kind: "sum", label: "Jumlah", val: data.usia.jumlah });
    rows.push({ kind: "heading", label: "Kelompok Pendidikan" });
    ["<D3", "D3", "S1", "S2", "S3"].forEach((k) => {
      rows.push({ kind: "row", label: k, val: data.pendidikan.rows[k] || 0 });
    });
    rows.push({ kind: "sum", label: "Jumlah", val: data.pendidikan.jumlah });
    rows.push({ kind: "heading", label: "Jenis Kelamin" });
    rows.push({ kind: "row", label: "Laki-Laki", val: data.jenis_kelamin.rows["Laki-Laki"] || 0 });
    rows.push({ kind: "row", label: "Perempuan", val: data.jenis_kelamin.rows["Perempuan"] || 0 });
    rows.push({ kind: "sum", label: "Jumlah", val: data.jenis_kelamin.jumlah });
    return rows;
  };
  const rows = buildRows();

  return (
    <div className="p-6 lg:p-10" data-testid="admin-demografi-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-teal-50 border border-teal-200 flex items-center justify-center text-teal-700">
          <Users size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 font-display">Demografi Pegawai</h1>
          <p className="text-xs text-slate-500">
            Komposisi pegawai aktif PT SUCOFINDO EPISI berdasarkan masa kerja, usia, pendidikan, dan jenis kelamin.
          </p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Bulan</Label>
            <Select value={bulan} onValueChange={setBulan}>
              <SelectTrigger className="mt-1.5" data-testid="demografi-filter-bulan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BULAN.map((b, i) => (<SelectItem key={i} value={String(i + 1)}>{b}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tahun</Label>
            <Select value={tahun} onValueChange={setTahun}>
              <SelectTrigger className="mt-1.5" data-testid="demografi-filter-tahun"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2].map((d) => {
                  const y = now.getFullYear() - d;
                  return (<SelectItem key={y} value={String(y)}>{y}</SelectItem>);
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button variant="outline" onClick={load} className="gap-1.5" data-testid="demografi-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button
              onClick={() => doExport("pdf")}
              disabled={exporting === "pdf"}
              className="bg-rose-600 hover:bg-rose-700 gap-1.5"
              data-testid="demografi-export-pdf-btn"
            >
              <FileDown size={14} /> {exporting === "pdf" ? "..." : "PDF"}
            </Button>
            <Button
              onClick={() => doExport("xlsx")}
              disabled={exporting === "xlsx"}
              className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
              data-testid="demografi-export-xlsx-btn"
            >
              <FileSpreadsheet size={14} /> {exporting === "xlsx" ? "..." : "Excel"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="font-extrabold text-slate-900 text-base font-display">PT SUCOFINDO EPISI</div>
          <div className="text-sm font-bold text-slate-700">JUMLAH PEGAWAI</div>
          <div className="text-[11px] text-slate-500 mt-1">
            Total pegawai aktif: <b>{data?.total_aktif ?? 0}</b>
          </div>
        </div>
        <Table data-testid="demografi-table">
          <TableHeader>
            <TableRow className="bg-slate-100 hover:bg-slate-100">
              <TableHead className="font-bold text-slate-800 uppercase text-xs tracking-wider text-center">URAIAN</TableHead>
              <TableHead className="font-bold text-slate-800 uppercase text-xs tracking-wider text-center w-[180px]">
                {periodeLabel}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={2} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={2} className="text-center py-10 text-slate-500">Belum ada data</TableCell></TableRow>
            ) : (
              rows.map((r, i) => {
                if (r.kind === "heading") {
                  return (
                    <TableRow key={i} className="bg-slate-50">
                      <TableCell colSpan={2} className="font-bold text-teal-800 font-display" data-testid={`demografi-heading-${r.label}`}>
                        {r.label}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (r.kind === "sum") {
                  return (
                    <TableRow key={i} className="bg-amber-50/60 border-t-2 border-amber-100">
                      <TableCell className="font-bold text-slate-900">{r.label}</TableCell>
                      <TableCell className="text-right font-bold text-slate-900 font-mono-clock">{r.val}</TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={i}>
                    <TableCell className="text-slate-700">{r.label}</TableCell>
                    <TableCell className="text-right font-mono-clock text-slate-800">{r.val}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
